import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import uuid from 'uuid/v1';
import { useTranslation } from 'react-i18next';
import {
  change,
  Field,
  FieldArray,
  Form,
  reduxForm,
  SubmissionError
} from 'redux-form';

import {
  CustomDateRangePicker,
  DropDown,
  FormActionButtons,
  Input,
  PopoverWrapper,
  ExpansionPanelWrapper,
  ScheduleDaysTimeDuration
} from 'components';

import { SCHEDULE_DAYS } from 'pages/create-activity/constants';

import validate from './validate';

import HelpIcon from '@material-ui/icons/Help';

import { useStyles } from './styles';

const initialValues = {
  availability: {
    startDate: moment(),
    endDate: null
  },
  cutoffPeriod: 0,
  cutoffPeriodTimeType: 'minutes',
  daysArray: [
    {
      id: uuid(),
      days: SCHEDULE_DAYS,
      startTimeAndDuration: [
        {
          startTime: moment(),
          duration: null,
          timeType: 'hours'
        }
      ]
    }
  ]
};

const ScheduleDateTime = ({
  onSubmit,
  handleSubmit,
  getPricesData,
  setSubmitButton,
  isUpdate,
  ...props
}) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const {
    scheduleDateTimeForm,
    pricesDailyScheduleForm,
    changeFieldValue
  } = props;
  const getExpansionPanelTitle = item => {
    return Object.keys(item.days)
      .filter(key => item.days[key])
      .map(key => t(key))
      .join(', ');
  };

  const getFormCategoriesById = item => {
    const day = pricesDailyScheduleForm.values.daysArray.find(
      day => day.id === item.id
    );

    if (day) {
      return day.categories;
    }

    const daysArray = pricesDailyScheduleForm.values.daysArray;
    const newDays = Object.keys(item.days).filter(field => item.days[field]);

    for (let i in daysArray) {
      if (daysArray.hasOwnProperty(i)) {
        const item = daysArray[i];
        const sameDays = item.days.filter(
          (day, index) => day === newDays[index]
        );
        if (sameDays.length === newDays.length) {
          return item.categories;
        }
      }
    }

    return [
      {
        participant: 'Adult',
        USD: '0',
        EUR: '0',
        NOK: '0'
      }
    ];
  };

  const onAddHandler = (fields, id) => {
    fields.push({
      days: SCHEDULE_DAYS,
      startTimeAndDuration: [
        {
          startTime: moment(),
          duration: null,
          timeType: 'hours'
        }
      ],
      id: id
    });
  };

  const getAllSelectedDays = () => {
    const daysArray = [...scheduleDateTimeForm.values.daysArray];
    let selectedDays = [];

    daysArray.map(item =>
      selectedDays.push(...Object.keys(item.days).filter(key => item.days[key]))
    );

    return selectedDays;
  };

  const submit = values => {
    const errors = validate(values);

    if (Object.keys(errors).length) {
      throw new SubmissionError(errors);
    }

    let days = values.daysArray.map(item => ({
      id: item.id,
      days: Object.keys(item.days).filter(field => item.days[field]),
      categories: getFormCategoriesById(item)
    }));

    const fixedPricesDays = days.map(item => {
      const categories = item.categories;
      return {
        id: uuid(),
        days: item.days,
        USD: (categories && categories[0]['USD']) || '0',
        EUR: (categories && categories[0]['EUR']) || '0',
        NOK: (categories && categories[0]['NOK']) || '0'
      };
    });

    changeFieldValue('pricesFixedForm', 'days', fixedPricesDays);
    changeFieldValue('pricesDailyScheduleForm', 'daysArray', days);
    getPricesData({
      daysArray: days
    });

    onSubmit(values);
  };

  return (
    <div>
      <Form onSubmit={handleSubmit(submit)}>
        <div className={classes.row}>
          <div className={classes.column}>
            <div className={classes.datePickerWrapper}>
              <Field
                name="availability"
                label="Availability"
                component={CustomDateRangePicker}
              />
            </div>
            <PopoverWrapper text="If you don't select the timestamps, the activity will be available all the time">
              <HelpIcon className={classes.helpIcon} />
            </PopoverWrapper>
          </div>
          <div className={classes.column}>
            <div className={classes.cutOffField}>
              <div className={classes.cutOffFieldWrapper}>
                <Field
                  name="cutoffPeriod"
                  isOnlyNumber={true}
                  maxLength={9}
                  placeholder="Number"
                  label="Cutoff period"
                  component={Input}
                />
              </div>

              <PopoverWrapper text="Cutoff period info sign">
                <HelpIcon className={classes.helpIcon} />
              </PopoverWrapper>
            </div>
            <div className={classes.cutOffTypeField}>
              <Field
                name="cutoffPeriodTimeType"
                list={['minutes', 'hours', 'days', 'weeks']}
                component={DropDown}
              />
            </div>
          </div>
        </div>

        <FieldArray
          name="daysArray"
          component={ExpansionPanelWrapper}
          isAddDeleteActions={true}
          disabledItemIndex={0}
          add={onAddHandler}
          getTitle={item => getExpansionPanelTitle(item)}
          tabName="schedule"
          tabNameAll="all schedules"
          children={(field, index) => (
            <ScheduleDaysTimeDuration
              field={field}
              index={index}
              disabledDays={getAllSelectedDays()}
            />
          )}
        />

        <FormActionButtons
          cancelPath="/accommodations-and-activities"
          actionLabel="Next"
          setSubmitButton={setSubmitButton}
          isUpdate={isUpdate}
        />
      </Form>
    </div>
  );
};

const mapStateToProps = state => ({
  scheduleDateTimeForm: state.form.scheduleDateTimeForm,
  pricesDailyScheduleForm: state.form.pricesDailyScheduleForm
});

const mapDispatchToProps = dispatch => ({
  changeFieldValue: (form, field, value) => {
    dispatch(change(form, field, value));
  }
});

export default reduxForm({
  form: 'scheduleDateTimeForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(ScheduleDateTime));
